'use client'

import { tw } from '@/lib/utils'

export type LabFit = 'initial' | 'tuned'

const FITS: LabFit[] = ['initial', 'tuned']

interface LabFitToggleProps {
  activeFit: LabFit
  /** Whether each fit has any scores — an unavailable fit renders disabled. */
  availability: Record<LabFit, boolean>
  labels: Record<LabFit, string>
  ariaLabel: string
  onSelect: (fit: LabFit) => void
  className?: string
}

// The Initial/Tuned segmented pill shared by the compact and wide lab score
// cards (dark-theme chrome — expects a navy ground).
export const LabFitToggle = ({
  activeFit,
  availability,
  labels,
  ariaLabel,
  onSelect,
  className,
}: LabFitToggleProps) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={tw('bg-navy/35 grid w-full grid-cols-2 rounded-full border border-white/15 p-1', className)}
  >
    <span
      aria-hidden
      className={tw(
        'pointer-events-none col-start-1 row-start-1 rounded-full bg-white transition-transform duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none',
        activeFit === 'tuned' && 'translate-x-full'
      )}
    />
    {FITS.map((fit) => {
      const active = fit === activeFit
      return (
        <button
          key={fit}
          type="button"
          aria-pressed={active}
          disabled={!availability[fit]}
          data-tracking="ignore"
          onClick={() => onSelect(fit)}
          className={tw(
            'relative z-10 row-start-1 inline-flex min-h-10 min-w-0 cursor-pointer items-center justify-center rounded-full px-3.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-80 disabled:cursor-not-allowed disabled:opacity-35',
            fit === 'initial' ? 'col-start-1' : 'col-start-2',
            active ? 'text-navy' : 'text-white/70 hover:text-white'
          )}
        >
          {labels[fit]}
        </button>
      )
    })}
  </div>
)
